import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, isSameWeek } from 'date-fns';
import { useAuth } from '../../../contexts/AuthContext';
import { usePrivacy } from '../../../contexts/PrivacyContext';

const WeeklyActivitiesChart = ({ activitiesData }) => {
    const containerRef = useRef(null);
    const { groupMembers } = useAuth();
    const { maskName } = usePrivacy();
    const [showInfo, setShowInfo] = useState(false);

    useEffect(() => {
        if (!containerRef.current || !activitiesData || activitiesData.length === 0) return;

        d3.select(containerRef.current).selectAll("svg").remove();

        const container = containerRef.current;
        const { width, height } = container.getBoundingClientRect();
        const margin = { top: 10, right: 20, bottom: 23, left: 45 };
        const chartWidth = width - margin.left - margin.right;
        const chartHeight = height - margin.top - margin.bottom;

        const svg = d3.select(container)
            .append("svg")
            .attr("viewBox", `0 0 ${width} ${height}`)
            .attr("preserveAspectRatio", "xMidYMid meet")
            .style("width", "100%")
            .style("height", "100%")
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Create member color map
        const memberColorMap = {};
        const allMembers = new Set();
        
        if (groupMembers && groupMembers.length > 0) {
            groupMembers.forEach(member => {
                if (member.email && member.color) {
                    memberColorMap[member.email] = member.color;
                    allMembers.add(member.email);
                }
            });
        }

        // Process data by weeks and members
        const weeklyData = processDataByWeeksAndMembers(activitiesData, memberColorMap, allMembers);
        
        if (weeklyData.length === 0) {
            svg.append("text")
                .attr("x", chartWidth / 2)
                .attr("y", chartHeight / 2)
                .attr("text-anchor", "middle")
                .style("font-size", "14px")
                .text("No data available for weekly activities chart");
            return;
        }

        // Get all unique members from the data
        const allMembersInData = new Set();
        weeklyData.forEach(week => {
            Object.keys(week.memberCounts).forEach(email => {
                allMembersInData.add(email);
            });
        });

        const members = Array.from(allMembersInData).sort((a, b) => {
            const getName = (email) => {
                const m = groupMembers?.find(m => m.email === email);
                return maskName(m?.name || email);
            };
        
            const aName = getName(a);
            const bName = getName(b);
        
            const aParts = aName.trim().split(/\s+/);
            const bParts = bName.trim().split(/\s+/);
        
            const aLast = aParts[aParts.length - 1].toLowerCase();
            const bLast = bParts[bParts.length - 1].toLowerCase();
        
            if (aLast !== bLast) return aLast.localeCompare(bLast);
        
            const aFirst = aParts[0].toLowerCase();
            const bFirst = bParts[0].toLowerCase();
        
            return aFirst.localeCompare(bFirst);
        });

        // Create scales for grouped bar chart
        const x0 = d3.scaleBand()
            .domain(weeklyData.map(d => d.weekLabel))
            .range([0, chartWidth])
            .padding(0.1);

        const x1 = d3.scaleBand()
            .domain(members)
            .range([0, x0.bandwidth()])
            .padding(0.05);

        const maxCount = d3.max(weeklyData, d => 
            d3.max(Object.values(d.memberCounts))
        );
        const yDomainMax = maxCount * 1.2;
        
        const y = d3.scaleLinear()
            .domain([0, yDomainMax])
            .nice()
            .range([chartHeight, 0]);

        // Create color scale
        const color = d3.scaleOrdinal()
            .domain(members)
            .range(members.map(email => memberColorMap[email] || '#cccccc'));

        // Add axes
        svg.append("g")
            .attr("transform", `translate(0,${chartHeight})`)
            .call(d3.axisBottom(x0))
            .selectAll("text")
            .attr("transform", "translate(26,5)")
            .style("text-anchor", "end");

        svg.append("g")
            .call(d3.axisLeft(y).ticks(5).tickFormat(d3.format("d")));

        // Add y-axis label
        svg.append("text")
            .attr("text-anchor", "middle")
            .attr("transform", "rotate(-90)")
            .attr("y", -margin.left + 15)
            .attr("x", -chartHeight / 2)
            .text("# Events");

        // Create grouped bars
        const weekGroups = svg.selectAll(".week-group")
            .data(weeklyData)
            .enter()
            .append("g")
            .attr("class", "week-group")
            .attr("transform", d => `translate(${x0(d.weekLabel)}, 0)`);

        // Add week separator lines
        svg.selectAll(".week-separator")
            .data(weeklyData.slice(0, -1)) // Don't add line after the last week
            .enter()
            .append("line")
            .attr("class", "week-separator")
            .attr("x1", d => x0(d.weekLabel) + x0.bandwidth() + x0.padding() * x0.bandwidth() / 2)
            .attr("y1", 0)
            .attr("x2", d => x0(d.weekLabel) + x0.bandwidth() + x0.padding() * x0.bandwidth() / 2)
            .attr("y2", chartHeight)
            .attr("stroke", "#666")
            .attr("stroke-width", 1.5)
            .attr("stroke-dasharray", "3,3")
            .style("opacity", 0.9);

        weekGroups.selectAll(".member-bar")
            .data(d => members.map(member => ({
                member: member,
                count: d.memberCounts[member] || 0,
                weekLabel: d.weekLabel
            })))
            .enter()
            .append("rect")
            .attr("class", "member-bar")
            .attr("x", d => x1(d.member))
            .attr("y", d => y(d.count))
            .attr("width", x1.bandwidth())
            .attr("height", d => chartHeight - y(d.count))
            .attr("fill", d => {
                const memberColor = color(d.member);
                const colorObj = d3.color(memberColor);
                if (colorObj) {
                    return colorObj.darker(0.7).toString();
                }
                return memberColor;
            })
            .attr("stroke", "white")
            .attr("stroke-width", 1)
            .style("position", "relative")
            .style("z-index", 10)
            .on("mouseover", function(event, d) {
                const memberName = getMemberName(d.member);
                
                if (d.count > 0) {
                    // Create tooltip
                    const tooltip = d3.select("body").append("div")
                        .attr("class", "tooltip")
                        .style("position", "absolute")
                        .style("background", "rgba(0, 0, 0, 0.8)")
                        .style("color", "white")
                        .style("padding", "8px")
                        .style("border-radius", "4px")
                        .style("font-size", "12px")
                        .style("pointer-events", "none")
                        .style("opacity", 0);

                    tooltip.html(`<div><strong>${memberName}</strong></div><div>Week: ${d.weekLabel}</div><div>Events: ${d.count}</div>`)
                        .style("left", (event.pageX + 10) + "px")
                        .style("top", (event.pageY - 10) + "px")
                        .transition()
                        .duration(200)
                        .style("opacity", 1);
                }
            })
            .on("mouseout", function() {
                d3.selectAll(".tooltip").remove();
            });

        function getMemberName(email) {
            if (groupMembers && groupMembers.length > 0) {
                const member = groupMembers.find(m => m.email === email);
                if (member && member.name) {
                    return maskName(member.name);
                }
            }
            return maskName(email.split('@')[0].substring(0, 10));
        }

    }, [activitiesData, groupMembers, maskName]);

    const processDataByWeeksAndMembers = (activities, memberColorMap, allMembers) => {
        if (!activities || activities.length === 0) return [];

        const dates = activities
            .filter(activity => activity.date)
            .map(activity => parseISO(activity.date));

        if (dates.length === 0) return [];

        const earliestDate = d3.min(dates);
        const latestDate = d3.max(dates);

        const firstWeekStart = startOfWeek(earliestDate, { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(latestDate, { weekStartsOn: 1 });

        const weeksInterval = eachDayOfInterval({ 
            start: firstWeekStart, 
            end: lastWeekEnd 
        }).filter(date => date.getDay() === 1);

        const weeklyData = weeksInterval.map(weekStart => {
            const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
            
            const activitiesInWeek = activities.filter(activity => {
                if (!activity.date) return false;
                const activityDate = parseISO(activity.date);
                return isSameWeek(activityDate, weekStart, { weekStartsOn: 1 });
            });

            const weekLabel = `${format(weekStart, 'MM/dd')}-${format(weekEnd, 'MM/dd')}`;
            
            // Count activities per member
            const memberCounts = {};
            
            // Initialize all members with 0
            allMembers.forEach(email => {
                memberCounts[email] = 0;
            });

            activitiesInWeek.forEach(activity => {
                if (activity.contributors && Array.isArray(activity.contributors)) {
                    activity.contributors.forEach(contributor => {
                        if (contributor.email && memberColorMap[contributor.email]) {
                            memberCounts[contributor.email] = (memberCounts[contributor.email] || 0) + 1;
                        }
                    });
                }
            });

            const total = Object.values(memberCounts).reduce((sum, count) => sum + count, 0);

            return {
                weekStart,
                weekEnd,
                weekLabel,
                memberCounts,
                total
            };
        });

        return weeklyData;
    };

    return (
        <ChartWrapper>
            <TitleContainer>
                <Title>
                    Weekly Activities by Member
                    <InfoIconContainer 
                        onMouseEnter={() => setShowInfo(true)}
                        onMouseLeave={() => setShowInfo(false)}
                    >
                        <InfoIcon>ⓘ</InfoIcon>
                        {showInfo && (
                            <InfoTooltip>
                                <InfoTitle>Weekly Activities Chart Information</InfoTitle>
                                <InfoItem>
                                    <strong>Grouped Bar Chart:</strong> Shows activity count for each team member per week
                                </InfoItem>
                                <InfoItem>
                                    <strong>Color Coding:</strong> Each member has a unique color based on their profile
                                </InfoItem>
                                <InfoItem>
                                    <strong>Week Separators:</strong> Dashed lines separate different weeks
                                </InfoItem>
                                <InfoItem>
                                    <strong>Bar Heights:</strong> Represent the number of activities each member contributed
                                </InfoItem>
                                <InfoItem>
                                    <strong>Interaction:</strong> Hover over bars to see member name, week, and event count
                                </InfoItem>
                            </InfoTooltip>
                        )}
                    </InfoIconContainer>
                </Title>
            </TitleContainer>
            <ChartContainer ref={containerRef} />
        </ChartWrapper>
    );
};

const ChartContainer = styled.div`
    flex: 1;   
    min-height: 0; 
    width: 100%;
    height: 100%;
    background-color: #f0f0f0;
    position: relative;
    overflow: hidden;
    display: flex;
    padding-left: 5px;
    padding-bottom: 5px;
    justify-content: center;
    align-items: center;
    & svg {
        max-width: 100%;
        max-height: 100%;
    }
`;

const ChartWrapper = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: visible;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0px;
    position: relative;
    flex-shrink: 0;
    height: 25px;
    overflow: visible;
`;

const Title = styled.h3`
    font-size: 14px;
    margin: 0;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #333;
`;

const InfoIconContainer = styled.div`
    position: relative;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const InfoIcon = styled.div`
    font-size: 12px;
    color: #666;
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #e0e0e0;
        border-color: #999;
        color: #333;
    }
`;

const InfoTooltip = styled.div`
    position: absolute;
    top: 25px;
    left: -280px;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    width: 300px;
    z-index: 9999;
`;

const InfoTitle = styled.h4`
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 10px 0;
    color: #333;
    text-align: center;
    border-bottom: 1px solid #eee;
    padding-bottom: 6px;
`;

const InfoItem = styled.div`
    font-size: 12px;
    margin: 6px 0;
    line-height: 1.4;
    color: #555;
`;

export default React.memo(WeeklyActivitiesChart); 